import { Box, Button, Flex, Icon, Spinner, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ProcessStepBar } from "../../../common/layout/ProcessStepBar";
import { EditContractStep, useQueryParams } from "../../../common/useQueryParams";
import { urls } from "../../../urls";
import { requireDataFields } from "../typeguards";
import { ContractStateTag } from "./ContractStateTag";
import { ContractTemplateTag } from "./ContractTemplateTag";
import { MissingDataFieldsBanner } from "./details/MissingDataFieldsBanner";
import { HeldBackNotice } from "./HeldBackNotice";
import type { SaveBeforeLeaving } from "./saveBeforeLeaving";
import { useContractState } from "./useContractState";

interface Props {
  navigateToStep: (step: EditContractStep) => void;
  navigateToEvent?: () => void;
  isSaving?: boolean;
  held?: SaveBeforeLeaving;
}

export const EditContractProcessNav = ({ navigateToStep, navigateToEvent, isSaving = false, held }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useQueryParams();
  const currentStep = params.getEditContractStep();
  const contractState = useContractState();
  const dataFields = requireDataFields(contractState.contract);
  const isPublished = contractState.contract.state === "Published";

  return (
    <Box width="full" maxWidth="full">
      <Flex align="center" justify="space-between" gap="3" pt="2" wrap="wrap">
        <Flex gap="2" align="center">
          {isPublished && (
            <Button
              size="xs"
              variant="ghost"
              color="smMuted"
              leftIcon={<Icon as={FaArrowLeft} />}
              onClick={() => navigate(urls.app.contracts.view.go(contractState.contract.id))}
            >
              {t("Back to contract")}
            </Button>
          )}
          {navigateToEvent && (
            <Button
              size="xs"
              variant="ghost"
              color="smMuted"
              leftIcon={<Icon as={FaArrowLeft} />}
              onClick={navigateToEvent}
            >
              {t("Back to sourcing event")}
            </Button>
          )}
        </Flex>
        <Flex gap="2" align="center">
          <ContractStateTag state={contractState.contract.state} />
          {contractState.contract.template && <ContractTemplateTag template={contractState.contract.template} />}
        </Flex>
      </Flex>
      <Box py="6">
        <ProcessStepBar
          steps={[
            { key: "details", label: t("Details") },
            { key: "docs", label: t("Documents") },
            { key: "review", label: t("Review") },
          ]}
          currentKey={currentStep}
          onStepClick={isSaving ? undefined : (key) => navigateToStep(key as EditContractStep)}
        />
        {isSaving && (
          <Flex justify="center" align="center" gap="2" mt="2" role="status">
            <Spinner size="xs" />
            <Text fontSize="sm" color="smMuted">
              {t("Saving your changes…")}
            </Text>
          </Flex>
        )}
        {held && <HeldBackNotice held={held} from="stepBar" />}
      </Box>
      {dataFields.missingDataFields.length > 0 && (
        <MissingDataFieldsBanner
          missingDataFields={dataFields.missingDataFields}
          missingCustomFields={dataFields.missingCustomFields ?? undefined}
        />
      )}
    </Box>
  );
};
